import { SHA256_RE, sha256Hex } from '../lib/hash.ts';
import type { KeyStore } from './keyStore.ts';

/** URI of a content-addressed blob inside documents: the API's `BlobRef` (`sha256:<hex>`). */
export const blobUri = (sha: string) => `sha256:${sha}`;

/** Local storage name of a blob (OPFS path / IndexedDB key); also the path inside backups. */
export const blobKey = (sha: string) => `blobs/${sha}`;

/** Hash of a blob URI: `sha256:<hex>` (current) or `blobs/<hex>` (phase-A local data). */
export function shaOfUri(uri: string): string | null {
  const m = /^(?:sha256:|blobs\/)([0-9a-f]{64})$/.exec(uri);
  return m ? m[1] : null;
}

/** Rewrites a legacy `blobs/<hex>` URI to `sha256:<hex>`; other URIs are returned unchanged. */
export function normalizeBlobUri(uri: string): string {
  const sha = shaOfUri(uri);
  return sha ? blobUri(sha) : uri;
}

/**
 * Content-addressed blobs (photos, renders, exports, thumbnails) keyed by SHA-256.
 * Identical files are stored once; object URLs are cached per hash for <img> tags.
 */
export class BlobStore {
  private readonly urls = new Map<string, string>();
  private readonly mimes = new Map<string, string>();
  private remote: ((sha: string) => Promise<Blob | null>) | null = null;

  /** Signed in: missing blobs are downloaded lazily (sync engine). Null = local only. */
  setRemoteFallback(fn: ((sha: string) => Promise<Blob | null>) | null): void {
    this.remote = fn;
  }

  constructor(readonly keys: KeyStore) {}

  get kind() {
    return this.keys.kind;
  }

  async put(data: Blob): Promise<string> {
    const sha = await sha256Hex(data);
    if (!(await this.keys.has(blobKey(sha)))) await this.keys.put(blobKey(sha), data);
    if (data.type) this.mimes.set(sha, data.type);
    return sha;
  }

  /** Stores `data` under a hash the caller already verified (backup import). */
  async putVerified(sha: string, data: Blob): Promise<void> {
    if (!SHA256_RE.test(sha)) throw new Error('Invalid blob hash.');
    if ((await sha256Hex(data)) !== sha) throw new Error('A file in the backup is damaged.');
    if (!(await this.keys.has(blobKey(sha)))) await this.keys.put(blobKey(sha), data);
  }

  /** The blob, downloading it first when it's only on the server. */
  async get(sha: string): Promise<Blob | null> {
    const local = await this.getLocal(sha);
    if (local || !this.remote) return local;
    return this.remote(sha);
  }

  /** The blob if it is on this device (never touches the network). */
  async getLocal(sha: string): Promise<Blob | null> {
    const blob = await this.keys.get(blobKey(sha));
    if (!blob) return null;
    const mime = this.mimes.get(sha);
    return mime && blob.type !== mime ? new Blob([blob], { type: mime }) : blob;
  }

  async getByUri(uri: string): Promise<Blob | null> {
    const sha = shaOfUri(uri);
    return sha ? this.get(sha) : null;
  }

  has(sha: string): Promise<boolean> {
    return this.keys.has(blobKey(sha));
  }

  async delete(sha: string): Promise<void> {
    const url = this.urls.get(sha);
    if (url) URL.revokeObjectURL(url);
    this.urls.delete(sha);
    await this.keys.delete(blobKey(sha));
  }

  async list(): Promise<string[]> {
    return (await this.keys.list('blobs/')).map((n) => n.slice('blobs/'.length)).filter((s) => SHA256_RE.test(s));
  }

  /** A cached object URL for <img src>, or null if the blob is missing. */
  async url(sha: string): Promise<string | null> {
    const cached = this.urls.get(sha);
    if (cached) return cached;
    const blob = await this.get(sha);
    if (!blob) return null;
    const url = URL.createObjectURL(blob);
    this.urls.set(sha, url);
    return url;
  }

  cachedUrl(sha: string): string | undefined {
    return this.urls.get(sha);
  }
}
